import { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Eye, ExternalLink } from "lucide-react";
import { GameBoxApi, GameBoxConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getFormattedDateTime } from "@/utils";
import { CreateMysteryBoxForm } from "./CreateMysteryBoxForm";

export function GamificationAllMysteryBoxes() {
  const [mysteryBoxes, setMysteryBoxes] = useState<GameBoxConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedMysteryBox, setSelectedMysteryBox] =
    useState<GameBoxConfig | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchMysteryBoxes();
  }, []);

  const fetchMysteryBoxes = async () => {
    setIsLoading(true);
    try {
      const response = await GameBoxApi.getAllGameBoxConfiguration({
        p_PageNumber: 1,
        p_PageSize: 10,
      });

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setMysteryBoxes(response.getAllGameBoxConfig || []);
      }
    } catch (error: unknown) {
      console.error("Error fetching mystery boxes:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch mystery boxes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "-";
    return getFormattedDateTime(dateString) || "-";
  };

  const handleView = (box: GameBoxConfig) => {
    setSelectedMysteryBox(box);
    setIsViewModalOpen(true);
  };

  const isPrizeActive = (isActive: number) => isActive === 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">
            All Mystery Boxes
          </h2>
          <p className="text-sm text-muted-foreground">
            View and manage all mystery box configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a
              href="/mystery-box"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Public Page
            </a>
          </Button>
          <Button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
          >
            <Plus className="h-4 w-4" />
            Create Mystery Box
          </Button>
        </div>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <DataTable
          title=""
          data={mysteryBoxes}
          cellClassName="py-6 text-gray-700"
          columns={[
            {
              header: "Title",
              accessorKey: "title" as keyof GameBoxConfig,
            },
            {
              header: "Guaranteed Win",
              accessorKey: "guaranteedWinFlag" as keyof GameBoxConfig,
              cell: (row: GameBoxConfig) => (
                <Badge
                  variant={row.guaranteedWinFlag ? "default" : "secondary"}
                  className="flex w-fit items-center gap-1"
                >
                  {row.guaranteedWinFlag ? "Yes" : "No"}
                </Badge>
              ),
            },
            {
              header: "Near Miss",
              accessorKey: "showNearMiss" as keyof GameBoxConfig,
              cell: (row: GameBoxConfig) => (
                <Badge
                  variant={row.showNearMiss ? "default" : "secondary"}
                  className="flex w-fit items-center gap-1"
                >
                  {row.showNearMiss ? "Yes" : "No"}
                </Badge>
              ),
            },
            {
              header: "Prizes",
              id: "prizes",
              cell: (row: GameBoxConfig) => row.prizes?.length?.toString() ?? "0",
            },
            {
              header: "Created Date",
              accessorKey: "createdDateTime" as keyof GameBoxConfig,
              cell: (row: GameBoxConfig) => formatDate(row.createdDateTime),
            },
            {
              header: "Action",
              id: "action",
              cell: (row: GameBoxConfig) => (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(row)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              ),
            },
          ]}
        />
      )}

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Mystery Box</DialogTitle>
          </DialogHeader>
          <CreateMysteryBoxForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              fetchMysteryBoxes();
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mystery Box Details</DialogTitle>
          </DialogHeader>
          {selectedMysteryBox && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-500">ID</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMysteryBox.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Title
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMysteryBox.title}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Image URL
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMysteryBox.imageUrl}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Guaranteed Win
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    <Badge
                      variant={
                        selectedMysteryBox.guaranteedWinFlag
                          ? "default"
                          : "secondary"
                      }
                    >
                      {selectedMysteryBox.guaranteedWinFlag ? "Yes" : "No"}
                    </Badge>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Show Near Miss
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    <Badge
                      variant={
                        selectedMysteryBox.showNearMiss ? "default" : "secondary"
                      }
                    >
                      {selectedMysteryBox.showNearMiss ? "Yes" : "No"}
                    </Badge>
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Display Text
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMysteryBox.displayText}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Terms Text
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMysteryBox.termsText}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Created Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedMysteryBox.createdDateTime)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Updated Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedMysteryBox.updatedDateTime)}
                  </p>
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-500">
                  Prizes
                </label>
                <div className="space-y-2">
                  {selectedMysteryBox.prizes?.length > 0 ? (
                    selectedMysteryBox.prizes.map((prize) => (
                      <div
                        key={prize.id}
                        className="rounded-md border p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {prize.prizeName}
                          </span>
                          <Badge
                            variant={
                              isPrizeActive(prize.isActive)
                                ? "default"
                                : "secondary"
                            }
                          >
                            {isPrizeActive(prize.isActive)
                              ? "Active"
                              : "Inactive"}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 md:grid-cols-3">
                          <span>Code: {prize.prizeCode}</span>
                          <span>Type: {prize.prizeType}</span>
                          <span>Value: {prize.prizeValue}</span>
                          <span>Per User Limit: {prize.perUserLimit}</span>
                          <span>Win Probability: {prize.winProbability}%</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No prizes configured</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
